import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Skeleton,
  TextField,
  ToggleButton,
  Typography
} from "@material-ui/core";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import { ScaleRangeStepper } from "components/__haim/Common/ScaleStepper";
import { PropertyTypeCheckboxList } from "components/__haim/Common/PropertyTypeCheckboxList";
import { PROPERTY_TYPE_GROUP_LABELS } from "utils/listings/propertyTypeGroups";
import { buildContactOriginOptions } from "utils/contacts/contactOrigin";
import { LocationPicker } from "../../../listingsMatch/LocationPicker";
import { ContactDialogInputTagsAutocomplete } from "../../../contacts/modal/ContactDialogInputTagsAutocomplete";
import { CrmStatusIcon } from "../../../contacts/crm/CrmIcons";
import { formatBudget, formatRooms } from "../../crmFormat";
import { formatAmountInput } from "./crmCellEditModel";
import { AMENITY_OPTIONS } from "utils/contacts/amenityLabels";
import { AmenityPicker } from "../../../contacts/AmenityPicker";
import {
  crmStatuses,
  crmStatusLabels,
  floors as FLOOR_SCALE,
  getContactCrmSides,
  getCrmSubStatusDefinitionsForSides,
  getContactOriginLabel,
  MAX_INTERESTED_IN_LOCATIONS,
  propertyTypes as PROPERTY_TYPE_VOCABULARY,
  rooms as ROOM_SCALE
} from "../../../../../@types/sharedSchema";
import type { CrmStatus, ProcessedListingAddress } from "../../../../../@types/sharedSchema";
import type { CrmContactRow } from "../../../../../@types/webotServer";

/**
 * The controls a CELL turns into. Each one is the app's OWN shared primitive wherever one
 * exists, so a fix to the rooms stepper, the tag vocabulary or the location registry
 * reaches this table for free.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * WHY EVERY CONTROL IS RENDERED ON AN ANCHORED SURFACE RATHER THAN INSIDE THE CELL
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * "click on a field in the table, and edit it in place" is the requirement, and the
 * literal reading — swap the cell's text for an `<input>` of the cell's width — does not
 * survive contact with the table it has to live in. `crmTablePlan.ts` lays these columns
 * out at their measured floors: **rooms 56px, cities 72, neighborhoods 72, tags 68,
 * propertyTypes 68, origin 68, customerTypes 56**. A text input inside 56px shows about
 * four characters; a two-bound room stepper does not fit at all; and a location
 * autocomplete at 72px cannot render a suggestion.
 *
 * So the editor mounts AT the cell and overlays outward from it, sized to
 * `max(cellWidth, control minimum)`. From the realtor's side that is still exactly what
 * was asked for — click the budget cell, a budget input appears there, type, click away —
 * and it is what every spreadsheet does with a cell whose editor is wider than its column.
 * What it is NOT is the rejected shape: one field, no title bar, no chips for the fields
 * you did not click, no ⤢, and no route to it except clicking the cell itself.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * THE PORTAL RULE — which surface a control gets, and why it is not a style choice
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * The host offers two surfaces, and each control declares which it needs
 * ({@link controlNeedsModalSurface}):
 *
 *  - **Popper** — no backdrop, so the page underneath keeps its hover and its scroll.
 *    Requires that the control render nothing outside its own DOM subtree.
 *  - **Popover (Modal)** — a backdrop, which also traps focus. Paid only where it must be.
 *
 * ⚠️ THE DIFFERENCE IS NO LONGER ABOUT DISMISSAL. The non-modal surface used to let the
 * dismissing click through to the next cell, so which column you were editing decided
 * whether moving on cost one click or two. It now swallows that click exactly as the
 * backdrop always did (`hooks/useOutsideDismiss.ts`), so the two surfaces behave identically and
 * this choice is purely about what the control needs to render.
 *
 * `LocationPicker` renders its suggestion list into a `Popper` PORTALED TO
 * `document.body`, and `ContactDialogInputTagsAutocomplete`'s listbox does the same. A
 * `ClickAwayListener` compares the event's composed path against its own DOM node, so a
 * click on a suggestion IS outside — it would close-and-save BEFORE the pick registered,
 * discarding the value just chosen. For locations that is not a lost keystroke: the field
 * is a WHOLE-ARRAY write, so the save would persist the array without the area the
 * realtor had just clicked.
 *
 * MUI's `Popover` is Modal-based instead: dismissal is its own backdrop and Escape,
 * delivered to `onClose` — one callback, no global listener, nothing that can mistake a
 * portal for the outside world. The nesting is proven: `EditContactDialog.tsx:1484`
 * renders this same `LocationPicker`, with the same `hideStreetResults` and the same
 * state-held `pickedLocations`, inside a MUI `Dialog` — the same Modal machinery, the same
 * focus trap, the same default z-index.
 *
 * Everything else is portal-free BY CONSTRUCTION, and that is a design constraint on this
 * file rather than a happy accident. It is also the reason three controls here are the
 * app's SECOND-choice component rather than its first, and each of those is a real
 * decision the app already made for the same reason:
 *
 *  - `crmStatus` is a plain `MenuList`, not a `Select`.
 *  - `contactOrigin` is the same `MenuList` shape, not the contact modal's
 *    `<TextField select>` — see {@link CellOrigin}.
 *  - `propertyTypes` is `PropertyTypeCheckboxList`, not `PropertyTypeSelect` — see
 *    {@link CellPropertyTypes}. That component exists precisely for this case and says so
 *    in its own header: *"for surfaces where a Select is the WRONG control — a narrow
 *    popover … nesting a Select's portalled menu inside one fights the outer click-away"*.
 *
 * Both taxonomies are still the app's shared ones (`groupPropertyTypes`,
 * `CONTACT_ORIGIN_PRESETS`), so only the PRESENTATION differs from the modal's; the
 * vocabulary cannot drift. Do not "tidy" any of the three into a `Select`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * TWO COMPATIBILITY SEAMS — `CellToggles` and `CellText` dispatch, for now
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `propertyTypes` and `contactOrigin` used to be a flat toggle grid and a free-text box
 * respectively, and `CrmCellEditor`'s switch still names those two generic controls for
 * them. Rather than leave the new pickers unreachable until that switch is edited, the two
 * generic controls DISPATCH to them — `CellToggles` on the vocabulary it was handed,
 * `CellText` on the placeholder the host passes.
 *
 * These are seams, not architecture. Deleting them is two lines in `CrmCellEditor.tsx`:
 *
 *     case "propertyTypes":  <CellPropertyTypes options={propertyTypeOptions(row)}
 *                              canonical={PROPERTY_TYPES} value={draft.values}
 *                              onChange={(values) => patchDraft({ values })} />
 *     case "contactOrigin":  <CellOrigin value={draft.text}
 *                              onChange={(text) => patchDraft({ text })}
 *                              onPick={(text) => commit({ text })} />
 *
 * The second line also BUYS something the seam cannot: `onPick` makes a lead-source pick
 * commit in the click that made it, the way `CellStatus` already does. Through the seam
 * the pick only patches the draft, so moving on costs one extra click.
 */

/**
 * Which of the host's two surfaces this dimension's control requires. See the portal rule.
 *
 * The eight document-only dimensions are all on the CHEAP surface, and that is a
 * constraint they were designed to meet rather than a discovery: floors is a stepper
 * pair, amenities is a checkbox grid, the money range and the built area are two text inputs
 * each, and the three scalars are single inputs. Not one of them renders a listbox. The moment a new control
 * here reaches for a `Select` or an `Autocomplete` it has to move to the modal surface —
 * see the portal rule above, which is a rule about correctness, not about looks.
 */
export const controlNeedsModalSurface = (dimension: string): boolean =>
  dimension === "cities" || dimension === "neighborhoods" || dimension === "tags";

/**
 * How wide the surface should be, at minimum, per dimension.
 *
 * The host takes `max(this, the cell's own width)`, so a wide column keeps its width and a
 * narrow one grows. These are the width each CONTROL needs to be usable, measured against
 * the control, not against the column.
 */
export const controlMinWidth = (dimension: string): number => {
  switch (dimension) {
    case "additionalNotes":
      return 360;
    case "cities":
    case "neighborhoods":
    case "tags":
      return 320;
    // 300 is the width `PropertyTypeCheckboxList` was designed against — its own header
    // names that number — and it is the four section headers that need it, not the type
    // names. The customer-type toggles happen to want the same: below ~300 the wrap puts
    // one chip per row.
    case "customerTypes":
    case "propertyTypes":
      return 300;
    case "rooms":
      return 260;
    // A menu row is an icon slot plus a label, and the longest label here is a system
    // origin rendered in Hebrew ("תמלול שיחה (AI)"), not one of the short presets. The
    // 68px column it opens from would otherwise wrap every second row.
    case "contactOrigin":
      return 260;
    case "crmStatus":
      return 240;
    // The 13 amenities as a two-column checkbox grid. One column would be a 13-row
    // scroller for a set the realtor wants to see all of at once.
    case "amenities":
      return 300;
    // Two money inputs side by side, each wide enough for "2,500,000" plus its ₪.
    case "budgetRange":
    case "budgetRentRange":
      return 300;
    // Two steppers, each "− קרקע +". The floor labels are words, not digits.
    case "floors":
      return 260;
    // Two short number boxes, each with its מ״ר.
    case "size":
      return 260;
    // An email is the one value here that is routinely long, and it is shown LTR inside
    // an RTL panel — cramping it is how a typo goes unnoticed.
    case "mail":
      return 260;
    default:
      return 200;
  }
};

// ───────────────────────────────────────────────────────────────────────────────
// Free text — name
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The placeholder the host passes for `contactOrigin`, and therefore the key `CellText`
 * dispatches on. Exported so the two sides name one string rather than two literals — see
 * the compatibility-seam note at the top of this file.
 */
export const ORIGIN_PLACEHOLDER = "מקור הליד";

/**
 * ONE line of text, focused and fully SELECTED on mount.
 *
 * The select-all is the difference between an editor and a cursor: a realtor who clicks a
 * name cell to correct a typo gets the caret where they clicked, but one who clicks it to
 * REPLACE the value would otherwise have to select the old text by hand first. Typing over
 * a selection is the spreadsheet behaviour, and Escape still restores.
 *
 * Lead source is NO LONGER free text — it is {@link CellOrigin}, and this component hands
 * it the field until the host's switch calls it directly. `caption` is dropped on that
 * branch: the picker shows Hebrew labels against raw values by construction, so
 * {@link originCaption}'s explanation has nothing left to explain.
 */
export function CellText({
  value,
  placeholder,
  caption,
  /**
   * A validation complaint about the CURRENT value, or null.
   *
   * ⚠️ IT LIVES HERE AND NOT IN THE HOST'S COMMIT PATH, and that placement is the whole
   * design. The editor saves on dismissal, so a refusal has to be something the realtor
   * can SEE while the surface is still open — a guard inside `commit` would be invisible
   * (the dismissing click has already been swallowed), would be bypassed entirely by the
   * unmount flush, and would strand `settledRef` in a state that makes the popup
   * unclosable. The control shows the problem; the host decides what a dismissal means.
   */
  error,
  /**
   * `ltr` for values that are ASCII by definition (an email) or have no strong character
   * at all (an id number). The ATTRIBUTE, never `sx={{ direction }}` — stylis-plugin-rtl
   * mirrors the CSS property and ships the opposite. See `crmFormat.ts`.
   */
  dir,
  inputMode,
  maxLength,
  onChange
}: {
  value: string;
  placeholder?: string;
  caption?: string | null;
  error?: string | null;
  dir?: "ltr";
  inputMode?: "text" | "email" | "numeric" | "tel";
  maxLength?: number;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.select();
  }, []);

  if (placeholder === ORIGIN_PLACEHOLDER) return <CellOrigin value={value} onChange={onChange} />;

  return (
    <>
      <TextField
        fullWidth
        size="small"
        autoFocus
        error={Boolean(error)}
        inputRef={ref}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ style: { textAlign: "start" }, dir, inputMode, maxLength }}
      />
      {error ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "error.main", fontWeight: 600 }}>
          {error}
        </Typography>
      ) : caption ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
          {caption}
        </Typography>
      ) : null}
    </>
  );
}

/**
 * @deprecated Nothing renders this any more — {@link CellOrigin} replaced the free-text
 * box it captioned, and a picker whose ROWS are labels against raw values needs no
 * footnote saying so. It survives only because `CrmCellEditor` still imports it; delete
 * both together when that switch is updated.
 */
export const originCaption = (raw: string): string | null => {
  const label = getContactOriginLabel(raw.trim());
  return label && label !== raw.trim() ? `מוצג בטבלה כ־"${label}"` : null;
};

// ───────────────────────────────────────────────────────────────────────────────
// Lead source
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The contact modal's lead-source list, as a `MenuList` — same values, same order, same
 * "type your own" escape hatch, one shared definition.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * THE ROW'S `value` IS RAW; ONLY ITS TEXT IS THE LABEL. Do not collapse the two.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `contactOrigin` holds free text, but every SYSTEM writer stamps one of eight canonical
 * ENGLISH values (`SYSTEM_CONTACT_ORIGINS`) — `AI call transcription`, `webhook`, `mcp`, …
 * — which `getContactOriginLabel` renders as Hebrew. So a row can read "תמלול שיחה (AI)"
 * while carrying `AI call transcription`, and a picker that wrote back what it displayed
 * would replace a value every system writer recognises with a Hebrew string none of them
 * do. The label is `ListItemText`'s child; the value is what `choose` emits. That split is
 * the whole reason this is a hand-rolled list and not `options.map(getContactOriginLabel)`.
 *
 * Which rows exist — and the folding-in of a stored value the presets do not contain — is
 * `buildContactOriginOptions`, shared verbatim with the modal so the two lists cannot
 * drift. It is read ONCE, at mount, deliberately: recomputing it from the live draft would
 * make the contact's original value vanish from the list the moment the realtor picked
 * something else, so "I picked wrong, put it back" would stop working.
 *
 * ⚠️ "מקור אחר…" IS FIRST, ABOVE THE PRESETS, IN BOTH PICKERS. Requested explicitly, and
 * it changes what the list is FOR: ten presets with a custom row buried under them reads
 * as a closed vocabulary with a footnote, and a realtor whose source is a specific
 * exhibition scrolls past ten wrong answers to find it.
 *
 * The custom input renders ABOVE the `MenuList` rather than inside it. MUI's `MenuList`
 * claims printable keystrokes for type-ahead item matching, so a `TextField` mounted as
 * one of its children swallows characters into focus navigation instead of the field.
 *
 * It is SEEDED WITH THE CURRENT VALUE and select-all'd, never blanked. This editor commits
 * on click-away, so an empty seed would turn a mis-click on "מקור אחר…" followed by a
 * click elsewhere into a silently cleared lead source; select-all keeps typing-to-replace
 * costless while making the destructive reading impossible.
 *
 * One row is deliberately absent and it looks like a bug: a contact holding `הוזן ידנית`
 * shows NOTHING ticked. That value is the "the realtor saved without picking a source"
 * sentinel the modal stamps on create — not a source — so offering it invites people to
 * select it as though it meant something. The modal excludes it for the same reason and
 * renders its placeholder. Nothing is written until a row is actually picked, so an
 * untouched open-and-dismiss leaves the sentinel exactly where it was.
 */
export function CellOrigin({
  value,
  onChange,
  /**
   * When supplied, a pick IS the commit — the `CellStatus` contract. Optional because the
   * host currently reaches this control through {@link CellText}, which has no `commit`
   * to hand over; see the compatibility-seam note at the top of this file.
   */
  onPick
}: {
  value: string;
  onChange: (next: string) => void;
  onPick?: (next: string) => void;
}) {
  const [custom, setCustom] = useState(false);
  const customRef = useRef<HTMLInputElement | null>(null);
  const [options] = useState(() => buildContactOriginOptions(value));

  useEffect(() => {
    if (custom) customRef.current?.select();
  }, [custom]);

  const current = value.trim();

  const choose = (next: string) => {
    setCustom(false);
    onChange(next);
    onPick?.(next);
  };

  const mark = (isSelected: boolean) =>
    isSelected ? <CheckRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} /> : <Box sx={{ width: 16 }} />;

  return (
    <Box>
      {custom && (
        <Box sx={{ px: 0.5, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            inputRef={customRef}
            value={value}
            placeholder="שם המקור (למשל: תערוכת נדל״ן)…"
            onChange={(event) => onChange(event.target.value)}
            inputProps={{ style: { textAlign: "start" } }}
          />
          <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
            נשמר בדיוק כפי שהוקלד · Enter שומר
          </Typography>
        </Box>
      )}

      {/* `autoFocusItem` only while the list is the thing being used — otherwise it fights
          the custom input for focus on the render that reveals it. */}
      <MenuList autoFocusItem={!custom} dense sx={{ py: 0, maxHeight: 320, overflowY: "auto" }}>
        <MenuItem selected={!current} onClick={() => choose("")} sx={{ gap: 1 }}>
          {mark(!current)}
          <ListItemText primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}>
            ללא מקור
          </ListItemText>
        </MenuItem>

        {!custom && (
          <MenuItem onClick={() => setCustom(true)} sx={{ gap: 1 }}>
            {mark(false)}
            <ListItemText primaryTypographyProps={{ variant: "body2", sx: { fontWeight: 600 } }}>
              ➕ מקור אחר…
            </ListItemText>
          </MenuItem>
        )}

        <Divider sx={{ my: 0.5 }} />

        {options.map((option) => {
          const isSelected = option === current;
          return (
            <MenuItem key={option} selected={isSelected} onClick={() => choose(option)} sx={{ gap: 1 }}>
              {mark(isSelected)}
              <ListItemText primaryTypographyProps={{ variant: "body2" }}>
                {getContactOriginLabel(option)}
              </ListItemText>
            </MenuItem>
          );
        })}
      </MenuList>
    </Box>
  );
}

/**
 * A note is prose: multiline, and Enter inserts a LINE BREAK rather than saving.
 *
 * Every other control here saves on Enter, and this one must not — a realtor writing two
 * sentences would have the first one committed and the editor torn down under them. The
 * host special-cases it (`⌘/Ctrl+Enter` saves instead), and the caption says so, because a
 * keyboard affordance nobody is told about is one nobody uses.
 */
export function CellNotes({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <>
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={10}
        size="small"
        autoFocus
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="הערות על הלקוח…"
      />
      <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
        Enter מוסיף שורה · ⌘/Ctrl+Enter שומר · Esc מבטל
      </Typography>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Money
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The money input — separators as you type, and the ONLY place they exist.
 *
 * `formatAmountInput` runs on every keystroke, so the field holds `2,500,000` while the
 * draft holds the same string and `parseAmount` strips it back to `2500000` exactly once,
 * at save time. Re-formatting the whole value on each keystroke (rather than inserting
 * separators positionally) is what keeps `2,500,00` from ever existing as an intermediate
 * state — there is no partial format to get wrong.
 *
 * ⚠️ THE `dir` ATTRIBUTE, NEVER `sx={{ direction: 'ltr' }}`. The app wraps everything in
 * stylis-plugin-rtl, which mirrors the CSS `direction` property along with the physical
 * ones — so the `sx` form ships as `direction: rtl` and does the exact opposite of what it
 * reads as. Measured on the shipped bundle. The attribute is not CSS, so it survives, and
 * it brings `unicode-bidi: isolate` from the UA stylesheet for free. `crmFormat.ts:15-22`
 * carries the full write-up.
 *
 * `storedFloor` is a minimum that PROVABLY exists on the document. It is shown and never
 * seeded, because this field writes `customerBudget` alone: without the caption the
 * realtor would be editing the top of a range whose bottom is invisible, and would read
 * the field as the contact's whole budget.
 */
export function CellMoney({
  value,
  storedFloor,
  hint,
  onChange
}: {
  value: string;
  storedFloor: number | null;
  hint?: string;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.select();
  }, []);

  return (
    <>
      <TextField
        fullWidth
        size="small"
        autoFocus
        inputRef={ref}
        value={value}
        placeholder="0"
        onChange={(event) => onChange(formatAmountInput(event.target.value))}
        inputProps={{ dir: "ltr", inputMode: "numeric", style: { textAlign: "start" } }}
        InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
      />
      {storedFloor !== null ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
          {/* `formatBudget` renders the Israeli form (`1.5 מיליון ₪`), which has no Latin
              LTR island to be reordered inside this Hebrew sentence. `₪1.5M` measured as
              `1.5M₪`, with the shekel migrating to the far side of its own number. */}
          מינימום שמור: {formatBudget(storedFloor)} · לעריכת הטווח פתחו את הכרטיס המלא
        </Typography>
      ) : hint ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
          {hint}
        </Typography>
      ) : null}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Rooms
// ───────────────────────────────────────────────────────────────────────────────

/**
 * `ScaleRangeStepper` DIRECTLY, not the `RoomRangePicker` wrapper around it.
 *
 * That wrapper copies `initialMin`/`initialMax` into `useState` on mount and ignores every
 * later change to them, so it holds a second copy of the value that can go stale. The
 * controlled primitive has no such state — and it owns the correction that keeps the pair
 * ordered (raising min above max pushes max up; lowering max below min pulls min down),
 * which is why the model writes both bounds whenever either moves.
 */
export function CellRooms({
  min,
  max,
  onChange
}: {
  min: number | null;
  max: number | null;
  onChange: (min: number | null, max: number | null) => void;
}) {
  return (
    <Box>
      <ScaleRangeStepper
        scale={ROOM_SCALE as unknown as number[]}
        min={min}
        max={max}
        minLabel="מ־"
        maxLabel="עד"
        formatValue={(value) => (value === ROOM_SCALE[ROOM_SCALE.length - 1] ? `${value}+` : value)}
        onChange={onChange}
      />
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
        {formatRooms(min, max) || "כל מספר החדרים"}
      </Typography>
    </Box>
  );
}

/**
 * A budget as a RANGE — two money inputs, min and max, ordered.
 *
 * ⚠️ THIS IS NOT {@link CellMoney} WITH A SECOND BOX, and the difference is which SOURCE
 * it reads. `CellMoney` serves the table, whose `budgetMin` column is
 * `COALESCE(customerBudgetMin, customerBudget)` — so it deliberately edits ONE number and
 * merely captions any provable floor, because a range control over that column would show
 * a "2M–2M" range the contact never entered and then persist it. This control serves a
 * host holding the DOCUMENT, where the two bounds are the raw stored fields and a range is
 * exactly what they are. `crmCellEditModel.ts` keeps them as two dimensions for that
 * reason; do not merge them.
 *
 * ORDERING IS CORRECTED HERE, not at patch time. Raising the minimum above the maximum
 * pushes the maximum up; lowering the maximum below the minimum pulls the minimum down —
 * the same rule `ScaleRangeStepper` applies to rooms and floors, and the same one the big
 * dialog applies to its own range pairs. Doing it in the control is what lets the model
 * stay a pure comparison, and it means the realtor SEES the correction rather than
 * discovering it after the save.
 *
 * Both boxes are cleared independently: an empty string parses to null, which is a real
 * value meaning "no bound".
 */
export function CellMoneyRange({
  min,
  max,
  hint,
  onChange
}: {
  min: string;
  max: string;
  hint?: string;
  onChange: (min: string, max: string) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.select();
  }, []);

  const asNumber = (text: string): number | null => {
    const digits = text.replace(/[^\d]/g, "");
    return digits ? Number(digits) : null;
  };

  /**
   * ⚠️ NO CORRECTION WHILE TYPING. This is the whole reason the two boxes behave the way
   * they do, and getting it wrong destroys a stored value in one keystroke.
   *
   * The maximum box is auto-focused and select-all'd, so a realtor replacing a budget
   * types the FIRST DIGIT of the new one over the old value. With a push-the-other-bound
   * rule on every keystroke, a contact stored as 1.5M–2.5M who is being retyped to 3M
   * passes through max = "3" — which is below the minimum, so the minimum is dragged down
   * to 3. By the time "3,000,000" is complete the floor is gone and nothing on screen
   * said so.
   *
   * The dialog on the same page already learned this and its comment states the rule:
   * free-typed numeric ranges "don't push while typing — that would drag the other bound
   * mid-keystroke. Instead they normalize on blur: an inverted pair is swapped." A SWAP,
   * not a clamp, because both numbers the realtor typed are real and the only thing wrong
   * is their order.
   *
   * `buildCellPatch` swaps too, and that is not redundant: this handler runs when focus
   * leaves the field, and the editor can commit on Enter or through the unmount flush
   * without ever blurring. The control makes the correction VISIBLE; the model makes it
   * TRUE.
   */
  const change = (bound: "min" | "max") => (raw: string): void => {
    const formatted = formatAmountInput(raw);
    if (bound === "min") onChange(formatted, max);
    else onChange(min, formatted);
  };

  const normalizeOnBlur = (): void => {
    const lo = asNumber(min);
    const hi = asNumber(max);
    // Only when BOTH bounds are set can they contradict — a lone bound is an open range.
    if (lo !== null && hi !== null && lo > hi) onChange(max, min);
  };

  const box = (bound: "min" | "max"): JSX.Element => (
    <TextField
      fullWidth
      size="small"
      label={bound === "min" ? "מ־" : "עד"}
      autoFocus={bound === "max"}
      inputRef={bound === "max" ? ref : undefined}
      value={bound === "min" ? min : max}
      placeholder="0"
      onChange={(event) => change(bound)(event.target.value)}
      onBlur={normalizeOnBlur}
      // `dir` on the INPUT, exactly as CellMoney does it — digits must never be reordered
      // by the RTL page, and the sx form would be mirrored by stylis into `rtl`.
      inputProps={{ dir: "ltr", inputMode: "numeric", style: { textAlign: "start" } }}
      InputProps={{ startAdornment: <InputAdornment position="start">₪</InputAdornment> }}
    />
  );

  return (
    <Box>
      {/* The MAXIMUM is focused on open, not the minimum. A budget is quoted as its
          ceiling — "עד 2.5 מיליון" — and 95% of budgeted contacts in the corpus carry a
          top with no floor at all, so the box the realtor came to type in is that one. */}
      <Box sx={{ display: "flex", gap: 1 }}>
        {box("min")}
        {box("max")}
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
        {hint ?? "אפשר למלא רק תקרה — מינימום ריק הוא טווח פתוח"}
      </Typography>
    </Box>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Built area
// ───────────────────────────────────────────────────────────────────────────────

/**
 * Built area (שטח בנוי) min/max in m² — the same two-box shape as {@link CellMoneyRange}, and
 * the same rule about it: NO correction while typing, a SWAP on blur. Retyping a minimum of
 * 80 into 120 passes through "1" and "12", both under a maximum of 100; pushing the other
 * bound on each keystroke would rewrite a number the realtor never touched.
 *
 * The MINIMUM is focused on open, the opposite of the budget pair, and for the same kind of
 * reason: a budget is quoted as its ceiling, a size as its floor ("מעל 100 מטר") — 99% of the
 * contacts that state a size state a minimum and 82% state nothing else (measured 2026-10-11).
 */
export function CellSizeRange({
  min,
  max,
  onChange
}: {
  min: string;
  max: string;
  onChange: (min: string, max: string) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    ref.current?.select();
  }, []);

  // Digits only, and no thousands separator: a size is three digits, and "1,20" mid-typing
  // reads as a mistake.
  const clean = (raw: string): string => raw.replace(/[^0-9]/g, "").slice(0, 5);
  const asNumber = (text: string): number | null => {
    const digits = clean(text);
    return digits ? Number(digits) : null;
  };

  const change = (bound: "min" | "max") => (raw: string): void => {
    if (bound === "min") onChange(clean(raw), max);
    else onChange(min, clean(raw));
  };

  const normalizeOnBlur = (): void => {
    const lo = asNumber(min);
    const hi = asNumber(max);
    // Only when BOTH bounds are set can they contradict — a lone bound is an open range.
    if (lo && hi && lo > hi) onChange(max, min);
  };

  const box = (bound: "min" | "max"): JSX.Element => (
    <TextField
      fullWidth
      size="small"
      label={bound === "min" ? "מ־" : "עד"}
      autoFocus={bound === "min"}
      inputRef={bound === "min" ? ref : undefined}
      value={bound === "min" ? min : max}
      onChange={(event) => change(bound)(event.target.value)}
      onBlur={normalizeOnBlur}
      // `dir` on the INPUT, exactly as CellMoney does it — digits must never be reordered
      // by the RTL page, and the sx form would be mirrored by stylis into `rtl`.
      inputProps={{ dir: "ltr", inputMode: "numeric", style: { textAlign: "start" } }}
      InputProps={{ endAdornment: <InputAdornment position="end">מ״ר</InputAdornment> }}
    />
  );

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        {box("min")}
        {box("max")}
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
        אפשר למלא רק מינימום. נכס שקטן ממנו בהרבה לא יוצע ללקוח בהתאמות.
      </Typography>
    </Box>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Floors
// ───────────────────────────────────────────────────────────────────────────────

/**
 * `ScaleRangeStepper` over the SHARED `floors` enum, for the same reason {@link CellRooms}
 * uses it over `rooms`: the wrapper components copy their bounds into state on mount and
 * never re-sync, and the primitive owns the min/max correction.
 *
 * ⚠️ THE SCALE IS `sharedSchema.floors` (−4 … 50), NOT the contact dialog's private
 * `DIALOG_FLOOR_SCALE` (−1 … 40). The shared one is the list `getClosestValidFloor` snaps
 * a saved floor back onto every time a search is seeded, so a value picked here is a value
 * that survives that round trip; a private scale would let this control emit a 44 that the
 * matcher then silently rewrites.
 *
 * ⚠️ AND 0 IS A REAL FLOOR. `crmCellEditModel`'s `isSetFloor` exists solely to keep the
 * money rule (0 is not a budget) away from this control — see the two helpers, which sit
 * next to each other precisely so nobody unifies them.
 */
export function CellFloors({
  min,
  max,
  onChange
}: {
  min: number | null;
  max: number | null;
  onChange: (min: number | null, max: number | null) => void;
}) {
  return (
    <Box>
      <ScaleRangeStepper
        scale={FLOOR_SCALE as unknown as number[]}
        min={min}
        max={max}
        minLabel="מ־"
        maxLabel="עד"
        formatValue={floorLabel}
        /* ⚠️ WITHOUT THIS THE FIRST TAP IS ABSURD. An empty stepper with no seed
           materialises a scale END, and this scale runs −4…50 — so "+" would offer
           מרתף 4 and "−" the 50th storey. Ground floor up, basement down: the same pair
           the contact dialog's own floor steppers have always passed. */
        emptySeed={{ up: 0, down: -1 }}
        onChange={onChange}
      />
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
        {describeFloors(min, max)}
      </Typography>
    </Box>
  );
}

/** −1 is a basement and 0 is the ground floor; both read as words, not numbers. */
const floorLabel = (value: number): string =>
  value < 0 ? `מרתף ${Math.abs(value)}` : value === 0 ? "קרקע" : String(value);

const describeFloors = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return "כל קומה";
  if (min !== null && max !== null) return min === max ? floorLabel(min) : `${floorLabel(min)} – ${floorLabel(max)}`;
  return min !== null ? `${floorLabel(min)} ומעלה` : `עד ${floorLabel(max as number)}`;
};

// ───────────────────────────────────────────────────────────────────────────────
// Amenities
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The 13 amenity booleans, as {@link AmenityPicker} draws them everywhere: the מרחב מוגן group
 * (ממ״ד / ממ״ק / מקלט) in its own block, then the rest as a two-column checkbox grid.
 *
 * ⚠️ ALL THIRTEEN, from `genericPropertyDetailsKeys` — never the listings filter's
 * `VISIBLE_BOOLEAN_FILTER_KEYS`. The chip this editor opens from renders the full list, so a
 * shorter editor would DISPLAY an amenity it cannot un-tick. `utils/contacts/amenityLabels.ts`
 * owns the list, the wording and the grouping, for exactly this kind of pairing.
 *
 * A checkbox grid rather than the toggle chips {@link CellToggles} uses for customer
 * types: those are seven values a contact usually has one of, so a chip that is on or off
 * reads instantly. These are thirteen requirements a contact routinely has five of, and at
 * that density a wrapped chip field stops being scannable.
 *
 * `onChange` hands back the WHOLE bag, every key present. The model then emits only the
 * keys that moved — an un-tick must reach the server as a literal `false`, and a partial
 * bag is how it would arrive as an omission instead, leaving the stored `true` in place.
 */
export function CellAmenities({
  value,
  onChange
}: {
  value: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}) {
  const selectedCount = AMENITY_OPTIONS.filter((option) => value[option.key]).length;

  return (
    <Box>
      <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
        <AmenityPicker value={value} onChange={onChange} />
      </Box>
      <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
        {selectedCount > 0 ? `${selectedCount} מאפיינים נבחרו` : "לא נבחרו מאפיינים"}
      </Typography>
    </Box>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Status
// ───────────────────────────────────────────────────────────────────────────────

/**
 * A plain `MenuList`, NOT a `Select`.
 *
 * A `Select` would portal its menu (see the portal rule at the top), which would force the
 * whole status editor onto the modal surface and cost a second click on the single most
 * frequently changed field on the page. Rendered inline, the list IS the editor: it is
 * already open when the cell opens, arrow keys move through it, and a pick saves and
 * closes in the one click the realtor already made.
 *
 * SUB-STATUSES ARE OFFERED UNDER `בטיפול` ONLY, and only for the contact's own SIDE. The
 * schema states the refinement is "meaningful only under בטיפול", and
 * `getContactCrmSides` is what keeps a buyer from being offered `חתימת בלעדיות` — with no
 * customer type set, both sides are offered, which is the same fallback `CrmStatusControl`
 * applies.
 *
 * There is no "save" here and no blur to wait for: picking a value IS the commit, which is
 * why {@link CellStatus} takes `onPick` rather than a draft setter.
 */
export function CellStatus({
  row,
  status,
  subStatus,
  onPick
}: {
  row: CrmContactRow;
  status: CrmStatus | null;
  subStatus: string | null;
  onPick: (status: CrmStatus | null, subStatus: string | null) => void;
}) {
  const sides = getContactCrmSides({ customerTypes: row.customerTypes } as never);
  const subDefinitions = getCrmSubStatusDefinitionsForSides(
    (sides.length ? sides : ["buyer", "seller"]) as never
  );

  const selectedRow = (isSelected: boolean) =>
    isSelected ? <CheckRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} /> : <Box sx={{ width: 16 }} />;

  return (
    <MenuList autoFocusItem dense sx={{ py: 0, maxHeight: 340, overflowY: "auto" }}>
      <MenuItem selected={!status} onClick={() => onPick(null, null)} sx={{ gap: 1 }}>
        {selectedRow(!status)}
        <ListItemText primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}>
          ללא סטטוס
        </ListItemText>
      </MenuItem>

      {/* Iterating `crmStatuses` rather than `Object.keys(crmStatusLabels)`: the labels map
          is keyed BY status, so its key order is an implementation detail of an object
          literal, while the array is the declared pipeline order the board and the filter
          chips already render in. */}
      {crmStatuses.map((option: CrmStatus) => {
        const isSelected = status === option && !subStatus;
        return (
          <MenuItem key={option} selected={isSelected} onClick={() => onPick(option, null)} sx={{ gap: 1 }}>
            {selectedRow(isSelected)}
            <CrmStatusIcon iconKey={option} sx={{ fontSize: 16, flexShrink: 0 }} />
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>{crmStatusLabels[option]}</ListItemText>
          </MenuItem>
        );
      })}

      {subDefinitions.length > 0 && (
        <Typography
          variant="caption"
          sx={{ display: "block", px: 2, pt: 1, pb: 0.5, color: "text.disabled", fontWeight: 600 }}
        >
          {crmStatusLabels.in_progress} — שלב
        </Typography>
      )}
      {subDefinitions.map((definition) => {
        const isSelected = status === "in_progress" && subStatus === definition.id;
        return (
          <MenuItem
            key={definition.id}
            selected={isSelected}
            // A sub-status is a refinement OF `in_progress`, so picking one implies the
            // status. Requiring the realtor to set `בטיפול` first and then reopen the cell
            // to refine it would be two round trips for one decision.
            onClick={() => onPick("in_progress", definition.id)}
            sx={{ gap: 1, pl: 3 }}
          >
            {selectedRow(isSelected)}
            <CrmStatusIcon iconKey={definition.icon} sx={{ fontSize: 16, flexShrink: 0 }} />
            <ListItemText primaryTypographyProps={{ variant: "body2" }}>{definition.label}</ListItemText>
          </MenuItem>
        );
      })}
    </MenuList>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Multi-select — customer types, property types
// ───────────────────────────────────────────────────────────────────────────────

/** The unknown values in a selection — the ones the server's `asEnumArray` will refuse. */
const unknownValues = (value: readonly string[], canonical: readonly string[]): string[] =>
  value.filter((entry) => entry && !canonical.includes(entry));

/**
 * The warning that makes a legacy import value FIXABLE rather than a 400 nobody can read.
 *
 * `optionsWithUnknown` deliberately keeps a value outside the canonical enum in the option
 * list (dropping it on seed would silently delete something the realtor never chose to
 * remove), and the server's `asEnumArray` REFUSES THE WHOLE SAVE when it sees one. So the
 * offending value has to be visible, deselectable, and explained.
 */
function UnknownValueWarning({ unknown, where }: { unknown: string[]; where?: string }) {
  if (!unknown.length) return null;
  return (
    <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "warning.main" }}>
      ערך שאינו ברשימה המוכרת (מיובא): {unknown.map((entry) => `"${entry}"`).join(", ")}
      {where ? ` — ${where}` : ""} · השרת יסרב לשמור עד שיוסר.
    </Typography>
  );
}

/**
 * `propertyTypes` — the app's OWN grouped picker, sectioned into דירות / צמודי קרקע /
 * מסחרי / אחר.
 *
 * The flat toggle wrap this replaced put all 33 of them in a 300px popover in the only
 * order the enum has — ALPHABETICAL, which for this vocabulary is close to random:
 * `אולמות` (event halls) leads, `דירה` sits 10th between `דיור מוגן` and `דירה מחולקת`,
 * and the twelve commercial types are scattered through the whole list. The app already
 * had the answer: `groupPropertyTypes()` is the shared taxonomy the contact modal's
 * `PropertyTypeSelect` renders, so the two now section identically and a newly added enum
 * member appears in both at once.
 *
 * `PropertyTypeCheckboxList`, not `PropertyTypeSelect`, and the difference is a click per
 * edit rather than a matter of taste — see the portal rule at the top of this file.
 * `PropertyTypeSelect` is a MUI `Select`, so its menu portals to `document.body`, a
 * `ClickAwayListener` reads a pick on it as "outside", and the whole dimension would have
 * to move to the modal surface. The checkbox list renders nothing outside its own subtree,
 * so `propertyTypes` stays on the cheap `Popper` — and it throws in per-section
 * select-all/none, which at 12 commercial types is the difference between one tap and
 * twelve.
 *
 * ⚠️ THE UNKNOWN-VALUE AFFORDANCE IS NOT FREE HERE. The toggle grid gave a legacy import
 * value a dashed warning border; a checkbox row has no such slot, and `groupPropertyTypes`
 * files anything unrecognised under "אחר" — correct (it stays reachable) but silent. So the
 * caption NAMES the value and says where to find it, which is strictly more useful than the
 * border was: at 37 rows, "one of these is wrong" is not a finding.
 */
export function CellPropertyTypes({
  options,
  value,
  canonical,
  onChange
}: {
  options: string[];
  value: string[];
  canonical: readonly string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <Box>
      <PropertyTypeCheckboxList options={options} value={value} onChange={onChange} maxHeight={300} />
      <UnknownValueWarning
        unknown={unknownValues(value, canonical)}
        where={`מופיע תחת "${PROPERTY_TYPE_GROUP_LABELS.other}"`}
      />
    </Box>
  );
}

/**
 * Is this the property-type vocabulary? A SET test, not `canonical === propertyTypes`:
 * reference equality would degrade silently the first time a caller spread the enum into a
 * fresh array, and a control quietly reverting to the old flat grid is the kind of
 * regression nobody files.
 */
const PROPERTY_TYPE_SET: ReadonlySet<string> = new Set(PROPERTY_TYPE_VOCABULARY);
const isPropertyTypeVocabulary = (canonical: readonly string[]): boolean =>
  canonical.length === PROPERTY_TYPE_VOCABULARY.length && canonical.every((entry) => PROPERTY_TYPE_SET.has(entry));

/**
 * STANDALONE toggles in a plain flex wrap — NOT a `ToggleButtonGroup`, and not a `Select`.
 *
 * Serves `customerTypes`: seven values, no published grouping, and they fit one wrap. When
 * handed the PROPERTY-TYPE vocabulary it defers to {@link CellPropertyTypes} instead — a
 * compatibility seam, explained at the top of this file, that keeps the grouped picker
 * reachable while `CrmCellEditor`'s switch still names this control for both dimensions.
 *
 * Not a `Select`, for the portal reason at the top of this file. Not a `ToggleButtonGroup`
 * because the group applies a negative inline margin to every child but the first so their
 * borders collapse into one strip, which assumes a single row; with seven types in a
 * ~300px container the row wraps, and that negative margin then pulls the first button of
 * the second row past the container edge. Measured at 390px in the quick editor: `עו״ד`
 * was sliced in half by the boundary.
 *
 * Labels are the raw enum values, which are already Hebrew — the same strings the row
 * itself prints two pixels away.
 *
 * `options` may contain values outside the canonical enum, put there by
 * `optionsWithUnknown` when the contact carries a legacy import value. That is deliberate:
 * the server's `asEnumArray` refuses the whole save when it sees one, so the value must be
 * VISIBLE and deselectable or the realtor gets a 400 they can neither read nor clear.
 */
export function CellToggles({
  options,
  value,
  canonical,
  onChange
}: {
  options: string[];
  value: string[];
  canonical: readonly string[];
  onChange: (next: string[]) => void;
}) {
  if (isPropertyTypeVocabulary(canonical)) {
    return <CellPropertyTypes options={options} value={value} canonical={canonical} onChange={onChange} />;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {options.map((option) => {
          const selected = value.includes(option);
          const unknown = !canonical.includes(option);
          return (
            <ToggleButton
              key={option}
              value={option}
              size="small"
              selected={selected}
              onChange={() =>
                onChange(selected ? value.filter((entry) => entry !== option) : [...value, option])
              }
              sx={{
                px: 1.25,
                minHeight: 32,
                border: "1px solid",
                borderRadius: 1,
                ...(unknown ? { borderStyle: "dashed", borderColor: "warning.main" } : {})
              }}
            >
              {option}
            </ToggleButton>
          );
        })}
      </Box>
      <UnknownValueWarning unknown={unknownValues(value, canonical)} />
    </Box>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Tags
// ───────────────────────────────────────────────────────────────────────────────

/**
 * The contact modal's OWN tag autocomplete, unchanged.
 *
 * It carries the realtor's own tag vocabulary (asked of the server each time its list opens,
 * `useContactTagSuggestions`), so the cell offers the tags this agent already uses instead of
 * an empty free-text box that invites a fourth spelling of "משקיע". Rebuilding it here to
 * dodge its portaled listbox would have forfeited exactly that, which is why tags pays for
 * the modal surface instead.
 */
export function CellTags({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  return <ContactDialogInputTagsAutocomplete small editable selectedTags={value} setSelectedTags={onChange} />;
}

// ───────────────────────────────────────────────────────────────────────────────
// Interested areas — the one control that cannot render off the row
// ───────────────────────────────────────────────────────────────────────────────

export type AreasHydration =
  | { status: "loading" }
  /** Resolved, but no accessible document came back — or the read failed. */
  | { status: "unavailable"; reason: "missing" | "error" }
  | { status: "ready" };

/**
 * ⚠️ THIS CONTROL IS GATED ON A DOCUMENT READ, AND THE GATE IS DATA-LOSS PREVENTION.
 *
 * `interestedInLocations` is a WHOLE-ARRAY write and the row carries only flattened,
 * unpaired `cities`/`neighborhoods` strings with the canonical ids stripped — so the
 * stored array is not reconstructible from anything the cell is handed. Rendering an empty
 * picker before the document arrives would show a contact's areas as blank and, on
 * dismiss, write that blank over the real ones: every area gone, every
 * `canonicalLocationId` gone with it, and the realtor's listing alerts silently stop
 * matching. The model refuses to emit the key until `status === 'ready'`
 * (`buildCellPatch`), and this component refuses to render an editable surface — two
 * guards, because the interaction model is "click away and it saves" and that dismiss can
 * land at any moment, including mid-fetch.
 *
 * `unavailable` is a real, expected state rather than an error: `get-details` scopes by the
 * contact document's own `organizationIds`, a write-time snapshot that is never backfilled
 * (measured: absent on 57.6% of the pilot org's contacts), while the Postgres list resolves
 * membership at query time — so the list can legitimately show a row this read then
 * declines to hydrate.
 *
 * Props that carry weight:
 *  - `hideStreetResults` — a contact is interested in a city or a neighbourhood, never in a
 *    specific street number. Same setting the contact modal uses for this field.
 *  - `includeEmpty` is left at its default TRUE. This picker writes the field listing
 *    ALERTS match against, so an area with no listings today is a legitimate pick that
 *    fires tomorrow. Only listing-SEARCH pickers pass false.
 *  - `pickedLocations` must be a referentially STABLE array (it is held in the host's draft
 *    state): the picker mirrors that prop into its own state on every change of it with no
 *    identity guard, so a fresh literal each render is an infinite loop.
 */
export function CellAreas({
  hydration,
  locations,
  onChange
}: {
  hydration: AreasHydration;
  locations: ProcessedListingAddress[];
  onChange: (next: ProcessedListingAddress[]) => void;
}) {
  if (hydration.status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="rectangular" height={38} sx={{ flex: 1, borderRadius: 1 }} />
        <CircularProgress size={14} />
      </Box>
    );
  }

  if (hydration.status === "unavailable") {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {hydration.reason === "error"
          ? "טעינת האזורים נכשלה — נסו שוב, או פתחו את הכרטיס המלא."
          : "לא ניתן להציג את האזורים מכאן. פתחו את הכרטיס המלא."}
      </Typography>
    );
  }

  return (
    <Box>
      {/* Streets stay hidden HERE only, unlike the contact dialog: the areas COLUMN
          this cell writes groups interests as city -> its neighborhoods
          (`interested_areas` in contactsPgQuery.ts drops street), so a street pick
          would render as a bare city — i.e. read as "anywhere in רמת גן" when the
          contact wants one street. Offering a pick the column then misreports is
          worse than not offering it; the picker now says so honestly instead of
          sitting on "התחל לכתוב לחיפוש". Drop this once the column carries streets. */}
      <LocationPicker
        maxPicks={MAX_INTERESTED_IN_LOCATIONS}
        pickedLocations={locations}
        onLocationsChange={onChange}
        hideStreetResults
        hideLabel
        small
      />
      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
        ערים ושכונות נשמרות יחד — שתי העמודות מתעדכנות.
      </Typography>
    </Box>
  );
}

/**
 * Enter / Escape, as one predicate, so the host does not repeat the check per control.
 *
 * Enter is `commit` everywhere EXCEPT the notes field, where it is a line break and
 * `⌘/Ctrl+Enter` commits instead — see {@link CellNotes}.
 *
 * ⚠️ `defaultPrevented` IS THE WHOLE GUARD, and dropping it breaks two controls.
 *
 * The host listens on a container ABOVE the control, and React bubbles synthetic events
 * from a portaled subtree up the COMPONENT tree — so every key an inner control handled
 * still reaches the host afterwards. Two of these controls use Enter and Escape for their
 * own purposes and mark them handled by calling `preventDefault()` without stopping
 * propagation:
 *
 *  - `Autocomplete` (tags, and the one inside `LocationPicker`) takes Enter to ACCEPT the
 *    highlighted suggestion. Committing on it as well would close the editor the instant
 *    the realtor picked a tag — and for locations, close it while the pick was still being
 *    applied, saving the array without the area just chosen.
 *  - `Autocomplete` also takes the FIRST Escape to close its listbox. Cancelling on that
 *    would tear the whole editor down when the realtor only meant to dismiss the
 *    suggestions; a second Escape, which the listbox no longer claims, correctly cancels.
 *
 * `MenuList` marks its own Enter the same way, so a keyboard status pick lands once rather
 * than as a pick plus a redundant commit.
 */
export const readEditorKey = (
  event: KeyboardEvent,
  multiline: boolean
): "commit" | "cancel" | null => {
  if (event.defaultPrevented) return null;
  if (event.key === "Escape") return "cancel";
  if (event.key !== "Enter") return null;
  if (multiline) return event.metaKey || event.ctrlKey ? "commit" : null;
  // A modifier + Enter inside a single-line field is still just Enter to a realtor.
  return "commit";
};
